import { adPlacements } from '../../../mocks/adPlacements';

interface AdPlacementGridProps {
  onSelectPlacement: (placementId: string) => void;
}

export default function AdPlacementGrid({ onSelectPlacement }: AdPlacementGridProps) {
  const getZoneColor = (zone: string) => {
    const colors: Record<string, string> = {
      sidebar: 'from-blue-500 to-blue-600',
      header: 'from-purple-500 to-purple-600',
      homepage: 'from-emerald-500 to-emerald-600',
      content: 'from-orange-500 to-orange-600',
      directory: 'from-teal-500 to-teal-600',
      news: 'from-rose-500 to-rose-600',
      events: 'from-cyan-500 to-cyan-600',
      mobile: 'from-pink-500 to-pink-600',
      footer: 'from-gray-500 to-gray-600'
    };
    return colors[zone] || 'from-gray-500 to-gray-600';
  };

  const getZoneIcon = (zone: string) => {
    const icons: Record<string, string> = {
      sidebar: 'ri-layout-right-2-line',
      header: 'ri-layout-top-2-line',
      homepage: 'ri-home-4-line',
      content: 'ri-file-text-line',
      directory: 'ri-building-line',
      news: 'ri-newspaper-line',
      events: 'ri-calendar-event-line',
      mobile: 'ri-smartphone-line',
      footer: 'ri-layout-bottom-2-line'
    };
    return icons[zone] || 'ri-advertisement-line';
  };

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {adPlacements.map((placement) => (
        <div
          key={placement.id}
          className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all border-2 ${
            placement.status === 'available' 
              ? 'border-emerald-200 hover:border-emerald-400' 
              : 'border-gray-200'
          }`}
        >
          {/* Header */}
          <div className={`bg-gradient-to-r ${getZoneColor(placement.zone)} text-white p-6 rounded-t-xl`}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg">
                <i className={`${getZoneIcon(placement.zone)} text-2xl`}></i>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                placement.status === 'available'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/20 text-white'
              }`}>
                {placement.status === 'available' ? 'Available' : 'Occupied'}
              </span>
            </div>
            <h3 className="text-xl font-bold mb-2">{placement.name}</h3>
            <p className="text-sm text-white/90">{placement.position}</p>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Dimensions</div>
                <div className="font-semibold text-gray-900">{placement.dimensions}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500 mb-1">Monthly Price</div>
                <div className="text-2xl font-bold text-emerald-600">${placement.price}</div>
              </div>
            </div>

            {/* Performance Stats */}
            {placement.status === 'occupied' && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-3">Current Performance</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Impressions</div>
                    <div className="font-semibold text-gray-900">{placement.impressions.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Clicks</div>
                    <div className="font-semibold text-gray-900">{placement.clicks.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">CTR</div>
                    <div className="font-semibold text-emerald-600">{placement.ctr}%</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-500">Current Advertiser</div>
                  <div className="font-medium text-gray-900">{placement.advertiser}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {placement.startDate} - {placement.endDate}
                  </div>
                </div>
              </div>
            )}

            {/* Action Button */}
            {placement.status === 'available' ? (
              <button
                onClick={() => onSelectPlacement(placement.id)}
                className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap"
              >
                Purchase This Spot
              </button>
            ) : (
              <button
                disabled
                className="w-full bg-gray-100 text-gray-400 py-3 rounded-lg font-semibold cursor-not-allowed whitespace-nowrap"
              >
                Currently Occupied
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}